import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store';
import { api } from '../utils/api';
import { usePopupHome, useHomeKey, openPopupHome, POPUP_REFRESH } from '../services/popupHome';   // one tab only (report c2846304)
import { tabColor } from '../tabPalette';

// ── "New approvals have arrived" (report 327a232d) ────────────────────────────────────────────────────
// "Remove all approval emails … and instead show a pop-up on the screen (in the SLF system) … that new
// approvals have arrived." Approval requests are no longer emailed (server services/outboundApproval);
// this is how an approver hears of one now, whatever page they are on. It asks every minute and again
// whenever the tab comes back (GET /api/approvals/pending-summary: what this login approves, never its
// own submissions), and puts the dialog up when something is waiting that it has not shown them yet.
// "Later" and "Open Approvals" both mark what it showed as seen — per login, in this browser — so it
// comes back only for the next new request; the Approvals badge still counts everything waiting.
// "Later" also holds the dialog off for an hour (report a162383f): "If a user selects New Approvals
// 'Later' do not show them new approvals for a 60-minute period." Marking seen alone let the very next
// request re-open the dialog at the next minute's poll, so an approver who had just said Later was
// interrupted again minutes later. The hour is kept per login in this browser like the seen list, and
// every tab honours it; what arrives within the hour waits and is shown together once it has lapsed.
// "Open Approvals" ends the hour.
// Asked in ONE browser tab (services/popupHome): the one showing the Approvals tab, else the one in
// front. Kept off the phone apps by NotOnPhoneApp in App.tsx (report fce7e84d).
const POLL_MS = 60_000;
const FIRST_MS = 3_000;
const SNOOZE_MS = 60 * 60_000;   // "Later": nothing is shown for 60 minutes (report a162383f)
// One line per kind that has something new, each in the words its own tab uses, and the Dashboard tab its Approvals list is on
// (report e58ddb81): Accounting's Approvals for the three documents and their emails, the Approvals button beside the Document
// box for Tracking and Filing, the one at the end of the Jobs box for job-complete emails.
const KINDS: Array<{ kind: string; label: string; home: string }> = [
  { kind: 'quotation', label: 'mod_quotation', home: 'outbox' },
  { kind: 'invoice', label: 'mod_invoice', home: 'outbox' },
  { kind: 'receipt', label: 'mod_receipt', home: 'outbox' },
  { kind: 'email', label: 'appr_arrived_email', home: 'outbox' },
  { kind: 'taken_return', label: 'tab_taken_return', home: 'approvals_doc' },
  { kind: 'filing', label: 'tab_upload_doc', home: 'approvals_doc' },
  { kind: 'job_complete', label: 'appr_arrived_job_complete', home: 'approvals_jobs' },
  { kind: 'email_check', label: 'appr_arrived_email_check', home: 'email_check' },   // the Approvals button in the Email box (report 4aaa018b)
];
// The tab the pop-up itself belongs on: the first of the kinds that has something new.
const homeOf = (kinds: string[]): string => KINDS.find(k => kinds.includes(k.kind))?.home || 'outbox';
type Item = { key: string; kind: string };

// The requests already put in front of this login, by key — trimmed to what is still waiting.
const seenKey = (userId: string) => `mf_apprSeen:${userId}`;
function readSeen(userId: string): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(seenKey(userId)) || '[]');
    return Array.isArray(v) ? v.map(String) : [];
  } catch { return []; }
}
function writeSeen(userId: string, keys: string[]): void {
  try { localStorage.setItem(seenKey(userId), JSON.stringify(keys)); } catch { /* not kept — it is shown again next time */ }
}

// Until when "Later" holds the dialog off (report a162383f): the moment it was pressed plus the hour, per
// login, in this browser. 0 = not held.
const snoozeKey = (userId: string) => `mf_apprSnooze:${userId}`;
function readSnooze(userId: string): number {
  try {
    const v = Number(localStorage.getItem(snoozeKey(userId)) || 0);
    return Number.isFinite(v) ? v : 0;
  } catch { return 0; }
}
function writeSnooze(userId: string, until: number): void {
  try {
    if (until > 0) localStorage.setItem(snoozeKey(userId), String(until));
    else localStorage.removeItem(snoozeKey(userId));
  } catch { /* not kept — the dialog is back at the next new request, as it was before the hour */ }
}

export default function ApprovalsArrived() {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const userId = me?.userId || '';
  const mine = !!userId && Object.values(me?.approves || {}).some(Boolean);
  const [items, setItems] = useState<Item[]>([]);
  const [, bump] = useState(0);

  const poll = useCallback(async () => {
    if (!userId || !localStorage.getItem('mf_token')) return;
    try {
      const d: any = await api.approvalsPending();
      const list: Item[] = Array.isArray(d?.items) ? d.items : [];
      // What is no longer waiting is forgotten, so a request that comes back (an approved record changed
      // and sent for approval again) is new again.
      const waiting = new Set(list.map(i => i.key));
      const seen = readSeen(userId);
      const kept = seen.filter(k => waiting.has(k));
      if (kept.length !== seen.length) writeSeen(userId, kept);
      setItems(list);
    } catch { /* transient — the next look brings it back */ }
  }, [userId]);

  useEffect(() => {
    if (!mine) { setItems([]); return; }
    const first = setTimeout(() => { void poll(); }, FIRST_MS);
    const id = setInterval(() => { void poll(); }, POLL_MS);
    // Again the moment this tab comes back into view or is clicked into (services/popupHome says so).
    const again = () => { void poll(); };
    // "Later" or "Open Approvals" pressed in another tab: what was seen there is seen here too, and so is
    // the hour that Later began, or Open Approvals ended (report a162383f).
    const shared = (e: StorageEvent) => { if (e.key === seenKey(userId) || e.key === snoozeKey(userId)) bump(n => n + 1); };
    window.addEventListener(POPUP_REFRESH, again);
    window.addEventListener('storage', shared);
    return () => {
      clearTimeout(first);
      clearInterval(id);
      window.removeEventListener(POPUP_REFRESH, again);
      window.removeEventListener('storage', shared);
    };
  }, [mine, poll, userId]);

  const seen = new Set(mine ? readSeen(userId) : []);
  const fresh = mine ? items.filter(i => !seen.has(i.key)) : [];
  // Within the hour after "Later" nothing is asked at all (report a162383f): the pop-up tells
  // services/popupHome it has nothing pending, so no tab draws the dialog or its card, and what arrives
  // meanwhile simply waits. The hour is read at every redraw — each minute's poll brings one — so the
  // dialog is back within a minute of the hour's end, with everything that arrived in it.
  const snoozed = mine && readSnooze(userId) > Date.now();
  const HOME = useHomeKey(homeOf(fresh.map(i => i.kind)), fresh.length > 0 && !snoozed);
  const home = usePopupHome(HOME, fresh.length > 0 && !snoozed, fresh.map(i => i.key));
  if (!fresh.length || snoozed || home === 'hidden') return null;

  const markSeen = () => {
    writeSeen(userId, Array.from(new Set(readSeen(userId).concat(items.map(i => i.key)))));
    bump(n => n + 1);
  };
  // "Later": seen, and nothing more for the next 60 minutes (report a162383f).
  const later = () => { writeSnooze(userId, Date.now() + SNOOZE_MS); markSeen(); };
  // The Approvals tab opens in the tab that already shows it, or in one of its own (services/popupHome).
  // Going there ends any hour a "Later" began — pressed in another tab a moment before this one heard of it.
  // A line goes to its own kind's tab (report e58ddb81); the button to the first of them.
  const open = (to: string = HOME) => { writeSnooze(userId, 0); markSeen(); openPopupHome(userId, to); };
  const lines = KINDS.map(k => ({ ...k, n: fresh.filter(i => i.kind === k.kind).length })).filter(k => k.n > 0);
  const accent = tabColor(HOME) || 'var(--accent)';

  // Dressed as the "Awaiting approval" pill every list already shows (components/ApprovalBadge).
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10080, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div role="dialog" aria-modal="true" aria-label={t('appr_arrived_title')}
        style={{ width: 420, maxWidth: '94vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', background: '#FEF3C7', borderBottom: '1px solid #FBD38D' }}>
          <i className="ti ti-clock-pause" style={{ fontSize: 20, color: '#B45309' }} aria-hidden />
          <div style={{ flex: 1, fontSize: 15, fontWeight: 800, color: '#92400E' }}>{t('appr_arrived_title')}</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
          {lines.map(k => (
            <div key={k.kind} role="button" tabIndex={0} onClick={() => open(k.home)} onKeyDown={e => { if (e.key === 'Enter') open(k.home); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{t(k.label)}</span>
              <span style={{ minWidth: 28, textAlign: 'center', fontSize: 12, fontWeight: 800, padding: '2px 9px', borderRadius: 999, background: '#FEF3C7', color: '#92400E', border: '1px solid #FBD38D' }}>{k.n}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, padding: '12px 18px', borderTop: '1px solid var(--border)' }}>
          <span style={{ flex: 1, fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('appr_arrived_total', { n: items.length })}</span>
          <button onClick={later}
            style={{ padding: '9px 18px', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {t('appr_arrived_later')}
          </button>
          <button onClick={() => open()}
            style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: accent, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {t('appr_arrived_open')}
          </button>
        </div>
      </div>
    </div>
  );
}
